import React, { useCallback, useMemo, useState } from 'react';
import { Box, Button, Flex, HStack, Text } from '@chakra-ui/react';
import { Card } from './Card';

interface SwitchCardProps {
  title?: string;
  data: { label: string; value: number; percent: number }[];
}

const SwitchCard: React.FC<SwitchCardProps> = ({ title, data }) => {
  const [selected, setSelected] = useState('number');

  const dataSum = useMemo(() => {
    return data.reduce((acc, element) => acc + element.value, 0);
  }, [data]);

  const formatValue = (value: number) => {
    if (selected === 'number') {
      if (value < 10) return `0${value}`;
      return `${value}`;
    } else {
      const percentValue = (value / dataSum) * 100;
      if (percentValue < 10) return `0${percentValue.toFixed(2)}%`;
      return `${percentValue.toFixed(2)}%`;
    }
  };

  const InfoRow = useCallback(
    ({
      data: { label, value, percent },
    }: {
      data: { label: string; value: number; percent: number };
    }) => {
      return (
        <Flex justifyContent="space-between" alignItems="center">
          <Text
            fontSize="12px"
            fontWeight={400}
            color="gray.500"
            alignSelf="center"
          >
            {label}
          </Text>
          <Box
            h={0}
            w="100%"
            border="dashed 1px"
            borderColor="gray.200"
            mx={2}
          />
          <Text
            fontSize="sm"
            fontWeight={600}
            color="button"
            alignSelf="center"
            mr={1}
          >
            {formatValue(value)}
          </Text>
          <Text fontSize="10px" color={percent > 0 ? 'positive' : 'negative'}>
            {percent > 0 ? `+${percent}%` : `${percent}%`}
          </Text>
        </Flex>
      );
    },
    [selected],
  );

  return (
    <Card>
      <Flex justifyContent="space-between" mb={4}>
        <Text fontSize="sm" fontWeight={500} color="button" alignSelf="center">
          {title}
        </Text>
        <HStack spacing={1}>
          <Button
            variant={selected === 'number' ? 'default' : 'switchNotSelected'}
            h="20px"
            w="0px"
            p="0px"
            borderRadius={4}
            size="sm"
            onClick={() => setSelected('number')}
          >
            01
          </Button>
          <Button
            variant={selected === 'percent' ? 'default' : 'switchNotSelected'}
            h="20px"
            w="0px"
            p="0px"
            borderRadius={4}
            size="sm"
            onClick={() => setSelected('percent')}
          >
            %
          </Button>
        </HStack>
      </Flex>
      {data.map((element, index) => (
        <InfoRow key={index} data={element} />
      ))}
    </Card>
  );
};

export default SwitchCard;
